import { Point2D } from './BezierMath';
import { CurveControlPoints } from './BezierGrid';

export type BezierLayoutPreset =
  'island' | 'isometric' | 'wave' | 'hourglass' |
  'fan' | 'spiral' | 'triangle' | 'valley' | 'cube' | 'spiderweb';

export interface LayoutData {
  corners: Point2D[][];
  hCurves: CurveControlPoints[][];
  vCurves: CurveControlPoints[][];
}

export interface LayoutConfig {
  patchCountX: number;
  patchCountY: number;
  activePatchMask?: boolean[][];
}

export function getLayoutConfig(preset: BezierLayoutPreset): LayoutConfig {
  switch (preset) {
    case 'triangle':
    case 'cube':
      return {
        patchCountX: 2,
        patchCountY: 2,
        activePatchMask: [
          [true, true],
          [false, true]
        ]
      };
    default:
      return { patchCountX: 3, patchCountY: 1 };
  }
}

export function generateLayout(
  preset: BezierLayoutPreset,
  canvasWidth: number,
  canvasHeight: number,
  patchCountX: number,
  patchCountY: number
): LayoutData {
  switch (preset) {
    case 'island': return generateIsland(canvasWidth, canvasHeight, patchCountX, patchCountY);
    case 'isometric': return generateIsometric(canvasWidth, canvasHeight, patchCountX, patchCountY);
    case 'wave': return generateWave(canvasWidth, canvasHeight, patchCountX, patchCountY);
    case 'hourglass': return generateHourglass(canvasWidth, canvasHeight, patchCountX, patchCountY);
    case 'fan': return generateFan(canvasWidth, canvasHeight, patchCountX, patchCountY);
    case 'spiral': return generateSpiral(canvasWidth, canvasHeight, patchCountX, patchCountY);
    case 'triangle': return generateTriangle(canvasWidth, canvasHeight);
    case 'valley': return generateValley(canvasWidth, canvasHeight, patchCountX, patchCountY);
    case 'cube': return generateCube(canvasWidth, canvasHeight);
    case 'spiderweb': return generateSpiderWeb(canvasWidth, canvasHeight);
  }
}

function defaultCurves(
  corners: Point2D[][],
  patchCountX: number,
  patchCountY: number
): { hCurves: CurveControlPoints[][]; vCurves: CurveControlPoints[][] } {
  const cornersY = patchCountY + 1;
  const cornersX = patchCountX + 1;

  const hCurves: CurveControlPoints[][] = [];
  for (let r = 0; r < cornersY; r++) {
    hCurves[r] = [];
    for (let c = 0; c < patchCountX; c++) {
      const left = corners[r][c];
      const right = corners[r][c + 1];
      hCurves[r][c] = {
        cp1: { x: left.x + (right.x - left.x) / 4, y: left.y + (right.y - left.y) / 4 },
        cp2: { x: left.x + (right.x - left.x) * 3 / 4, y: left.y + (right.y - left.y) * 3 / 4 }
      };
    }
  }

  const vCurves: CurveControlPoints[][] = [];
  for (let r = 0; r < patchCountY; r++) {
    vCurves[r] = [];
    for (let c = 0; c < cornersX; c++) {
      const top = corners[r][c];
      const bottom = corners[r + 1][c];
      vCurves[r][c] = {
        cp1: { x: top.x + (bottom.x - top.x) / 4, y: top.y + (bottom.y - top.y) / 4 },
        cp2: { x: top.x + (bottom.x - top.x) * 3 / 4, y: top.y + (bottom.y - top.y) * 3 / 4 }
      };
    }
  }

  return { hCurves, vCurves };
}

function generateIsland(
  cw: number, ch: number, patchCountX: number, patchCountY: number
): LayoutData {
  const marginX = cw * 0.10;
  const marginY = ch * 0.14;
  const areaW = cw - marginX * 2;
  const areaH = ch - marginY * 2;
  const cornersY = patchCountY + 1;
  const cornersX = patchCountX + 1;

  const corners: Point2D[][] = [];
  for (let r = 0; r < cornersY; r++) {
    corners[r] = [];
    const ty = patchCountY > 1 ? r / patchCountY : r;
    const ny = ty * 2 - 1;

    for (let c = 0; c < cornersX; c++) {
      const tx = c / patchCountX;
      const nx = tx * 2 - 1;
      let px = marginX + tx * areaW;
      let py = marginY + ty * areaH;

      if (r === 0 || r === cornersY - 1) {
        const pullAmount = areaH * 0.18 * nx * nx;
        py += r === 0 ? pullAmount : -pullAmount;
      }
      if (c === 0 || c === cornersX - 1) {
        const pullAmount = areaW * 0.06 * ny * ny;
        px += c === 0 ? pullAmount : -pullAmount;
      }
      corners[r][c] = { x: px, y: py };
    }
  }

  const hBulge = areaH * 0.09;
  const hCurves: CurveControlPoints[][] = [];
  for (let r = 0; r < cornersY; r++) {
    hCurves[r] = [];
    for (let c = 0; c < patchCountX; c++) {
      const left = corners[r][c];
      const right = corners[r][c + 1];
      const midParam = (c + 0.5) / patchCountX;
      const sinEnvelope = Math.sin(midParam * Math.PI);
      const isTop = r === 0;
      const isBottom = r === cornersY - 1;

      let yOff1: number, yOff2: number;
      if (isTop) {
        const bulge = -hBulge * (0.5 + 0.5 * sinEnvelope);
        yOff1 = bulge * 1.1;
        yOff2 = bulge * 0.9;
      } else if (isBottom) {
        const bulge = hBulge * (0.5 + 0.5 * sinEnvelope);
        yOff1 = bulge * 0.9;
        yOff2 = bulge * 1.1;
      } else {
        const sign = (r + c) % 2 === 0 ? 1 : -1;
        yOff1 = sign * 14;
        yOff2 = -sign * 14;
      }

      hCurves[r][c] = {
        cp1: { x: left.x + (right.x - left.x) / 3, y: left.y + (right.y - left.y) / 3 + yOff1 },
        cp2: { x: left.x + (right.x - left.x) * 2 / 3, y: left.y + (right.y - left.y) * 2 / 3 + yOff2 }
      };
    }
  }

  const vBulge = areaW * 0.05;
  const vCurves: CurveControlPoints[][] = [];
  for (let r = 0; r < patchCountY; r++) {
    vCurves[r] = [];
    for (let c = 0; c < cornersX; c++) {
      const top = corners[r][c];
      const bottom = corners[r + 1][c];
      const midParam = patchCountY > 1 ? (r + 0.5) / patchCountY : 0.5;
      const sinEnvelope = Math.sin(midParam * Math.PI);
      const isLeft = c === 0;
      const isRight = c === cornersX - 1;

      let xOff1: number, xOff2: number;
      if (isLeft) {
        const bulge = -vBulge * (0.5 + 0.5 * sinEnvelope);
        xOff1 = bulge * 1.1;
        xOff2 = bulge * 0.9;
      } else if (isRight) {
        const bulge = vBulge * (0.5 + 0.5 * sinEnvelope);
        xOff1 = bulge * 0.9;
        xOff2 = bulge * 1.1;
      } else {
        const sign = (r + c) % 2 === 0 ? 1 : -1;
        xOff1 = sign * 12;
        xOff2 = -sign * 12;
      }

      vCurves[r][c] = {
        cp1: { x: top.x + (bottom.x - top.x) / 3 + xOff1, y: top.y + (bottom.y - top.y) / 3 },
        cp2: { x: top.x + (bottom.x - top.x) * 2 / 3 + xOff2, y: top.y + (bottom.y - top.y) * 2 / 3 }
      };
    }
  }

  return { corners, hCurves, vCurves };
}

function generateIsometric(
  cw: number, ch: number, _patchCountX: number, _patchCountY: number
): LayoutData {
  const cx = cw / 2;
  const cy = ch / 2;
  const hw = cw * 0.42;
  const hh = ch * 0.44;
  const pinch = cw * 0.02;

  // Top row: left-mid, near-apex-left, near-apex-right, right-mid
  // Bottom row: near-nadir-left, near-center-left, near-center-right, near-nadir-right
  const corners: Point2D[][] = [
    [
      { x: cx - hw, y: cy },
      { x: cx - pinch, y: cy - hh },
      { x: cx + pinch, y: cy - hh },
      { x: cx + hw, y: cy }
    ],
    [
      { x: cx - hw * 0.52, y: cy + hh },
      { x: cx - pinch, y: cy + hh * 0.08 },
      { x: cx + pinch, y: cy + hh * 0.08 },
      { x: cx + hw * 0.52, y: cy + hh }
    ]
  ];

  const hCurves: CurveControlPoints[][] = [];

  // Top row curves
  hCurves[0] = [];
  // Left side to apex-left: curves upward and inward (upper-left face edge)
  hCurves[0][0] = {
    cp1: { x: cx - hw * 0.72, y: cy - hh * 0.35 },
    cp2: { x: cx - hw * 0.35, y: cy - hh * 0.70 }
  };
  // Apex-left to apex-right: nearly flat across the top
  hCurves[0][1] = {
    cp1: { x: cx - pinch * 0.3, y: cy - hh * 1.02 },
    cp2: { x: cx + pinch * 0.3, y: cy - hh * 1.02 }
  };
  // Apex-right to right side: curves downward and outward (upper-right face edge)
  hCurves[0][2] = {
    cp1: { x: cx + hw * 0.35, y: cy - hh * 0.70 },
    cp2: { x: cx + hw * 0.72, y: cy - hh * 0.35 }
  };

  // Bottom row curves
  hCurves[1] = [];
  // Nadir-left to center-left: curves inward and upward
  hCurves[1][0] = {
    cp1: { x: cx - hw * 0.45, y: cy + hh * 0.72 },
    cp2: { x: cx - hw * 0.18, y: cy + hh * 0.35 }
  };
  // Center-left to center-right: short bridge across center
  hCurves[1][1] = {
    cp1: { x: cx - pinch * 0.3, y: cy + hh * 0.06 },
    cp2: { x: cx + pinch * 0.3, y: cy + hh * 0.06 }
  };
  // Center-right to nadir-right: curves outward and downward
  hCurves[1][2] = {
    cp1: { x: cx + hw * 0.18, y: cy + hh * 0.35 },
    cp2: { x: cx + hw * 0.45, y: cy + hh * 0.72 }
  };

  const vCurves: CurveControlPoints[][] = [];
  vCurves[0] = [];

  // Left outer edge: mid-left down to nadir-left (bulges left for hex silhouette)
  vCurves[0][0] = {
    cp1: { x: cx - hw * 1.05, y: cy + hh * 0.30 },
    cp2: { x: cx - hw * 0.82, y: cy + hh * 0.68 }
  };
  // Inner-left vertical: apex-left down to center-left (V-shape inward)
  vCurves[0][1] = {
    cp1: { x: cx - pinch * 1.5, y: cy - hh * 0.35 },
    cp2: { x: cx - pinch * 1.5, y: cy - hh * 0.05 }
  };
  // Inner-right vertical: apex-right down to center-right (V-shape inward)
  vCurves[0][2] = {
    cp1: { x: cx + pinch * 1.5, y: cy - hh * 0.35 },
    cp2: { x: cx + pinch * 1.5, y: cy - hh * 0.05 }
  };
  // Right outer edge: mid-right down to nadir-right (bulges right for hex silhouette)
  vCurves[0][3] = {
    cp1: { x: cx + hw * 1.05, y: cy + hh * 0.30 },
    cp2: { x: cx + hw * 0.82, y: cy + hh * 0.68 }
  };

  return { corners, hCurves, vCurves };
}

function generateWave(
  cw: number, ch: number, patchCountX: number, patchCountY: number
): LayoutData {
  const marginX = cw * 0.08;
  const marginY = ch * 0.18;
  const areaW = cw - marginX * 2;
  const areaH = ch - marginY * 2;
  const amplitude = areaH * 0.25;
  const cornersX = patchCountX + 1;
  const cornersY = patchCountY + 1;

  const corners: Point2D[][] = [];
  for (let r = 0; r < cornersY; r++) {
    corners[r] = [];
    const ty = patchCountY > 1 ? r / patchCountY : r;
    for (let c = 0; c < cornersX; c++) {
      const tx = c / patchCountX;
      const sineOffset = Math.sin(tx * Math.PI * 2) * amplitude;
      const yBase = marginY + ty * areaH;
      corners[r][c] = {
        x: marginX + tx * areaW,
        y: yBase + sineOffset
      };
    }
  }

  const { hCurves, vCurves } = defaultCurves(corners, patchCountX, patchCountY);

  // Add wave curvature to horizontal curves
  for (let r = 0; r < cornersY; r++) {
    for (let c = 0; c < patchCountX; c++) {
      const tMid = (c + 0.5) / patchCountX;
      const curvature = Math.cos(tMid * Math.PI * 2) * amplitude * 0.6;
      hCurves[r][c].cp1.y += curvature * 0.8;
      hCurves[r][c].cp2.y += curvature * 1.2;
    }
  }

  return { corners, hCurves, vCurves };
}

function generateHourglass(
  cw: number, ch: number, patchCountX: number, patchCountY: number
): LayoutData {
  const marginX = cw * 0.08;
  const marginY = ch * 0.12;
  const areaW = cw - marginX * 2;
  const areaH = ch - marginY * 2;
  const cornersX = patchCountX + 1;
  const cornersY = patchCountY + 1;
  const pinchAmount = areaW * 0.30;

  const corners: Point2D[][] = [];
  for (let r = 0; r < cornersY; r++) {
    corners[r] = [];
    const ty = patchCountY > 1 ? r / patchCountY : r;
    for (let c = 0; c < cornersX; c++) {
      const tx = c / patchCountX;
      const nx = tx * 2 - 1;
      // Pinch at vertical center (ty=0.5 for multi-patch, or use per-row approach)
      const pinchFactor = r === 0 ? 0 : 1;
      const xOffset = nx > 0
        ? -pinchAmount * pinchFactor
        : pinchAmount * pinchFactor;

      corners[r][c] = {
        x: marginX + tx * areaW + (c > 0 && c < cornersX - 1 ? 0 : xOffset),
        y: marginY + ty * areaH
      };
    }
  }

  // For single patchY, both rows are edges. Make left/right pinch inward at center.
  // Top row: wide spread
  // Bottom row: also wide spread
  // Use vertical curves to pinch in the middle
  const { hCurves, vCurves } = defaultCurves(corners, patchCountX, patchCountY);

  // Pinch vertical curves inward at their midpoint
  for (let r = 0; r < patchCountY; r++) {
    for (let c = 0; c < cornersX; c++) {
      const top = corners[r][c];
      const bottom = corners[r + 1][c];
      const midX = (top.x + bottom.x) / 2;
      const centerX = cw / 2;
      const pullToCenter = (midX - centerX) * 0.55;

      vCurves[r][c].cp1.x -= pullToCenter;
      vCurves[r][c].cp2.x -= pullToCenter;
    }
  }

  return { corners, hCurves, vCurves };
}

function generateFan(
  cw: number, ch: number, patchCountX: number, patchCountY: number
): LayoutData {
  const marginX = cw * 0.08;
  const marginY = ch * 0.10;
  const areaW = cw - marginX * 2;
  const areaH = ch - marginY * 2;
  const cornersX = patchCountX + 1;
  const cornersY = patchCountY + 1;
  const narrowWidth = areaW * 0.12;
  const cx = cw / 2;

  const corners: Point2D[][] = [];
  for (let r = 0; r < cornersY; r++) {
    corners[r] = [];
    const ty = patchCountY > 1 ? r / patchCountY : r;
    // Interpolate width from full at top to narrow at bottom
    const width = areaW * (1 - ty) + narrowWidth * ty;
    const left = cx - width / 2;

    for (let c = 0; c < cornersX; c++) {
      const tx = c / patchCountX;
      corners[r][c] = {
        x: left + tx * width,
        y: marginY + ty * areaH
      };
    }
  }

  const { hCurves, vCurves } = defaultCurves(corners, patchCountX, patchCountY);
  return { corners, hCurves, vCurves };
}

// Isometric cube layout for 2x2 patch grid.
// Three faces of equal area forming a hexagonal outline.
//
// Corner grid:
//   [0][0]=BotLeft  [0][1]=Left     [0][2]=Apex
//         | P00 (left) | P01 (diamond)|
//   [1][0]=Nadir    [1][1]=Hub      [1][2]=Right
//         | P10 (off)  | P11 (right)  |
//   [2][0]=Nadir    [2][1]=Nadir    [2][2]=BotRight

function diamondLayout(cw: number, ch: number): Point2D[][] {
  const cx = cw * 0.50;
  const cy = ch * 0.48;
  const hw = cw * 0.30;
  const hh = ch * 0.42;

  const apex: Point2D =     { x: cx, y: cy - hh };
  const left: Point2D =     { x: cx - hw, y: cy - hh * 0.5 };
  const right: Point2D =    { x: cx + hw, y: cy - hh * 0.5 };
  const hub: Point2D =      { x: cx, y: cy };
  const botLeft: Point2D =  { x: cx - hw, y: cy + hh * 0.5 };
  const botRight: Point2D = { x: cx + hw, y: cy + hh * 0.5 };
  const nadir: Point2D =    { x: cx, y: cy + hh * 0.85 };

  return [
    [botLeft, left, apex],
    [nadir, hub, right],
    [nadir, nadir, botRight]
  ];
}

function flattenBottomCurves(
  corners: Point2D[][],
  hCurves: CurveControlPoints[][],
  vCurves: CurveControlPoints[][]
): void {
  const bl = corners[0][0];
  const nad = corners[1][0];
  const nad2 = corners[2][1];
  const br = corners[2][2];
  const bow = Math.abs(nad.y - bl.y) * 0.08;

  // BotLeft → Nadir: bow outward (down-left)
  vCurves[0][0] = {
    cp1: {
      x: bl.x + (nad.x - bl.x) * 0.33 - bow * 0.4,
      y: bl.y + (nad.y - bl.y) * 0.33 + bow
    },
    cp2: {
      x: bl.x + (nad.x - bl.x) * 0.67 - bow * 0.15,
      y: bl.y + (nad.y - bl.y) * 0.67 + bow
    }
  };

  // Nadir → BotRight: bow outward (down-right)
  hCurves[2][1] = {
    cp1: {
      x: nad2.x + (br.x - nad2.x) * 0.33 + bow * 0.15,
      y: nad2.y + (br.y - nad2.y) * 0.33 + bow
    },
    cp2: {
      x: nad2.x + (br.x - nad2.x) * 0.67 + bow * 0.4,
      y: nad2.y + (br.y - nad2.y) * 0.67 + bow
    }
  };
}

function generateSpiral(
  cw: number, ch: number, patchCountX: number, patchCountY: number
): LayoutData {
  const marginX = cw * 0.10;
  const marginY = ch * 0.12;
  const areaW = cw - marginX * 2;
  const areaH = ch - marginY * 2;
  const cornersX = patchCountX + 1;
  const cornersY = patchCountY + 1;
  const twistAmount = areaW * 0.15;

  const corners: Point2D[][] = [];
  for (let r = 0; r < cornersY; r++) {
    corners[r] = [];
    const ty = patchCountY > 1 ? r / patchCountY : r;
    // Top row shifts right, bottom row shifts left (or vice versa)
    const rowShift = (r === 0 ? -1 : 1) * twistAmount;

    for (let c = 0; c < cornersX; c++) {
      const tx = c / patchCountX;
      const baseX = marginX + tx * areaW;
      // Skew: shift proportional to distance from center
      const distFromCenter = tx - 0.5;
      const skew = distFromCenter * rowShift;

      corners[r][c] = {
        x: baseX + skew,
        y: marginY + ty * areaH + (r === 0 ? -1 : 1) * Math.abs(distFromCenter) * areaH * 0.08
      };
    }
  }

  const { hCurves, vCurves } = defaultCurves(corners, patchCountX, patchCountY);

  // Add twist to vertical curves
  for (let r = 0; r < patchCountY; r++) {
    for (let c = 0; c < cornersX; c++) {
      const tx = c / patchCountX;
      const twist = (tx - 0.5) * twistAmount * 0.5;
      vCurves[r][c].cp1.x += twist;
      vCurves[r][c].cp2.x -= twist;
    }
  }

  return { corners, hCurves, vCurves };
}

function generateTriangle(cw: number, ch: number): LayoutData {
  // Adjust positions to create more uniform patch aspect ratios
  const botLeft: Point2D =  { x: cw * 0.06, y: ch * 0.86 };
  const left: Point2D =     { x: cw * 0.20, y: ch * 0.46 };
  const apex: Point2D =     { x: cw * 0.50, y: ch * 0.08 };
  const hub: Point2D =      { x: cw * 0.50, y: ch * 0.60 };
  const right: Point2D =    { x: cw * 0.80, y: ch * 0.46 };
  const botRight: Point2D = { x: cw * 0.94, y: ch * 0.86 };
  const nadir: Point2D =    { x: cw * 0.50, y: ch * 0.92 };

  const corners: Point2D[][] = [
    [botLeft, left, apex],
    [nadir, hub, right],
    [nadir, nadir, botRight]
  ];

  const { hCurves, vCurves } = defaultCurves(corners, 2, 2);

  const s = Math.min(cw, ch) * 0.025;

  // Inner edges: make them perfectly linear for uniform cell spacing
  // This ensures the grid cells have consistent proportions where patches meet

  // hCurves[1][0]: Nadir→Hub (bottom boundary of P00)
  const nadirHubDx = hub.x - nadir.x;
  const nadirHubDy = hub.y - nadir.y;
  hCurves[1][0].cp1.x = nadir.x + nadirHubDx * 0.33;
  hCurves[1][0].cp1.y = nadir.y + nadirHubDy * 0.33;
  hCurves[1][0].cp2.x = nadir.x + nadirHubDx * 0.67;
  hCurves[1][0].cp2.y = nadir.y + nadirHubDy * 0.67;

  // hCurves[1][1]: Hub→Right (bottom boundary of P01)
  const hubRightDx = right.x - hub.x;
  const hubRightDy = right.y - hub.y;
  hCurves[1][1].cp1.x = hub.x + hubRightDx * 0.33;
  hCurves[1][1].cp1.y = hub.y + hubRightDy * 0.33;
  hCurves[1][1].cp2.x = hub.x + hubRightDx * 0.67;
  hCurves[1][1].cp2.y = hub.y + hubRightDy * 0.67;

  // vCurves[0][1]: Left→Hub (right boundary of P00)
  const leftHubDx = hub.x - left.x;
  const leftHubDy = hub.y - left.y;
  vCurves[0][1].cp1.x = left.x + leftHubDx * 0.33;
  vCurves[0][1].cp1.y = left.y + leftHubDy * 0.33;
  vCurves[0][1].cp2.x = left.x + leftHubDx * 0.67;
  vCurves[0][1].cp2.y = left.y + leftHubDy * 0.67;

  // vCurves[1][1]: Hub→Nadir (left boundary of P11)
  const hubNadirDx = nadir.x - hub.x;
  const hubNadirDy = nadir.y - hub.y;
  vCurves[1][1].cp1.x = hub.x + hubNadirDx * 0.33;
  vCurves[1][1].cp1.y = hub.y + hubNadirDy * 0.33;
  vCurves[1][1].cp2.x = hub.x + hubNadirDx * 0.67;
  vCurves[1][1].cp2.y = hub.y + hubNadirDy * 0.67;

  // Outer boundary edges: gentle outward bulge
  // Left edge: BotLeft→Left — bulge left
  hCurves[0][0].cp1.x -= s * 0.9;  hCurves[0][0].cp1.y -= s * 0.4;
  hCurves[0][0].cp2.x -= s * 0.9;  hCurves[0][0].cp2.y -= s * 0.4;

  // Upper-left: Left→Apex — bulge up-left
  hCurves[0][1].cp1.x -= s * 0.6;  hCurves[0][1].cp1.y -= s * 0.8;
  hCurves[0][1].cp2.x -= s * 0.6;  hCurves[0][1].cp2.y -= s * 0.8;

  // Upper-right: Apex→Right — bulge up-right
  vCurves[0][2].cp1.x += s * 0.6;  vCurves[0][2].cp1.y -= s * 0.8;
  vCurves[0][2].cp2.x += s * 0.6;  vCurves[0][2].cp2.y -= s * 0.8;

  // Right edge: Right→BotRight — bulge right
  vCurves[1][2].cp1.x += s * 0.9;  vCurves[1][2].cp1.y -= s * 0.4;
  vCurves[1][2].cp2.x += s * 0.9;  vCurves[1][2].cp2.y -= s * 0.4;

  // Bottom-left: BotLeft→Nadir — gentle downward bow
  vCurves[0][0].cp1.y += s * 0.8;
  vCurves[0][0].cp2.y += s * 0.8;

  // Bottom-right: Nadir→BotRight — gentle downward bow
  hCurves[2][1].cp1.y += s * 0.8;
  hCurves[2][1].cp2.y += s * 0.8;

  return { corners, hCurves, vCurves };
}

function generateValley(
  cw: number, ch: number, patchCountX: number, patchCountY: number
): LayoutData {
  const marginX = cw * 0.08;
  const marginY = ch * 0.10;
  const areaW = cw - marginX * 2;
  const areaH = ch - marginY * 2;
  const cornersX = patchCountX + 1;
  const cornersY = patchCountY + 1;
  const valleyDepth = areaH * 0.30;

  const corners: Point2D[][] = [];
  for (let r = 0; r < cornersY; r++) {
    corners[r] = [];
    const ty = patchCountY > 1 ? r / patchCountY : r;
    for (let c = 0; c < cornersX; c++) {
      const tx = c / patchCountX;
      const nx = tx * 2 - 1;
      let py = marginY + ty * areaH;

      // V-shape dip in center for both rows
      const dip = valleyDepth * (1 - Math.abs(nx));
      if (r === 0) {
        py += dip; // Top row dips down in center
      } else {
        py -= dip * 0.5; // Bottom row dips up in center
      }

      corners[r][c] = { x: marginX + tx * areaW, y: py };
    }
  }

  const { hCurves, vCurves } = defaultCurves(corners, patchCountX, patchCountY);

  // Sharpen the V by adjusting control points
  for (let r = 0; r < cornersY; r++) {
    for (let c = 0; c < patchCountX; c++) {
      const tMid = (c + 0.5) / patchCountX;
      const nxMid = tMid * 2 - 1;
      const sign = r === 0 ? 1 : -0.5;
      const extra = valleyDepth * 0.3 * (1 - Math.abs(nxMid)) * sign;
      hCurves[r][c].cp1.y += extra;
      hCurves[r][c].cp2.y += extra;
    }
  }

  return { corners, hCurves, vCurves };
}

function generateCube(cw: number, ch: number): LayoutData {
  const corners = diamondLayout(cw, ch);
  const { hCurves, vCurves } = defaultCurves(corners, 2, 2);
  flattenBottomCurves(corners, hCurves, vCurves);

  const b = Math.min(cw, ch) * 0.035;
  // Inward pull on outer edges for concave silhouette
  hCurves[0][0].cp1.x += b;  hCurves[0][0].cp2.x += b;
  hCurves[0][1].cp1.x += b * 0.5;  hCurves[0][1].cp1.y += b;
  hCurves[0][1].cp2.x += b * 0.5;  hCurves[0][1].cp2.y += b;
  vCurves[0][2].cp1.x -= b * 0.5;  vCurves[0][2].cp1.y += b;
  vCurves[0][2].cp2.x -= b * 0.5;  vCurves[0][2].cp2.y += b;
  vCurves[1][2].cp1.x -= b;  vCurves[1][2].cp2.x -= b;

  return { corners, hCurves, vCurves };
}

function generateSpiderWeb(cw: number, ch: number): LayoutData {
  const cx = cw * 0.50;
  const cy = ch * 0.50;
  const r0 = Math.min(cw, ch) * 0.005; // Center point (very tight)
  const r1 = Math.min(cw, ch) * 0.28; // Middle ring
  const r2 = Math.min(cw, ch) * 0.48; // Outer ring

  // Create a 3x2 radial web: center patch + outer ring
  // Row 0: tiny center "circle" (approximated as one patch)
  // Row 1: inner ring (3 patches)
  // Row 2: outer ring (3 patches)
  const angle1 = -Math.PI / 2; // Starting angle (top)

  const corners: Point2D[][] = [
    // Center patch corners - nearly coincident at center
    [
      { x: cx + r0 * Math.cos(angle1), y: cy + r0 * Math.sin(angle1) },
      { x: cx + r0 * Math.cos(angle1 + Math.PI * 2/3), y: cy + r0 * Math.sin(angle1 + Math.PI * 2/3) },
      { x: cx + r0 * Math.cos(angle1 + Math.PI * 4/3), y: cy + r0 * Math.sin(angle1 + Math.PI * 4/3) },
      { x: cx + r0 * Math.cos(angle1 + Math.PI * 2), y: cy + r0 * Math.sin(angle1 + Math.PI * 2) }
    ],
    // Inner ring
    [
      { x: cx + r1 * Math.cos(angle1), y: cy + r1 * Math.sin(angle1) },
      { x: cx + r1 * Math.cos(angle1 + Math.PI * 2/3), y: cy + r1 * Math.sin(angle1 + Math.PI * 2/3) },
      { x: cx + r1 * Math.cos(angle1 + Math.PI * 4/3), y: cy + r1 * Math.sin(angle1 + Math.PI * 4/3) },
      { x: cx + r1 * Math.cos(angle1 + Math.PI * 2), y: cy + r1 * Math.sin(angle1 + Math.PI * 2) }
    ],
    // Outer ring
    [
      { x: cx + r2 * Math.cos(angle1), y: cy + r2 * Math.sin(angle1) },
      { x: cx + r2 * Math.cos(angle1 + Math.PI * 2/3), y: cy + r2 * Math.sin(angle1 + Math.PI * 2/3) },
      { x: cx + r2 * Math.cos(angle1 + Math.PI * 4/3), y: cy + r2 * Math.sin(angle1 + Math.PI * 4/3) },
      { x: cx + r2 * Math.cos(angle1 + Math.PI * 2), y: cy + r2 * Math.sin(angle1 + Math.PI * 2) }
    ]
  ];

  const { hCurves, vCurves } = defaultCurves(corners, 3, 2);

  // Make horizontal curves circular arcs
  const kappa = 0.5522847498; // Bezier approximation of circle
  for (let r = 0; r < 3; r++) {
    const radius = r === 0 ? r0 : (r === 1 ? r1 : r2);
    for (let c = 0; c < 3; c++) {
      const startAngle = angle1 + c * Math.PI * 2/3;
      const endAngle = angle1 + (c + 1) * Math.PI * 2/3;
      const arcLength = (endAngle - startAngle) * radius;
      const handleLength = arcLength * kappa / (Math.PI / 2);

      const start = corners[r][c];
      const end = corners[r][c + 1];

      const startTangentAngle = startAngle + Math.PI / 2;
      const endTangentAngle = endAngle + Math.PI / 2;

      hCurves[r][c] = {
        cp1: {
          x: start.x + handleLength * Math.cos(startTangentAngle),
          y: start.y + handleLength * Math.sin(startTangentAngle)
        },
        cp2: {
          x: end.x - handleLength * Math.cos(endTangentAngle),
          y: end.y - handleLength * Math.sin(endTangentAngle)
        }
      };
    }
  }

  // Radial spokes should be straight
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < 4; c++) {
      const inner = corners[r][c];
      const outer = corners[r + 1][c];
      vCurves[r][c] = {
        cp1: { x: inner.x + (outer.x - inner.x) * 0.33, y: inner.y + (outer.y - inner.y) * 0.33 },
        cp2: { x: inner.x + (outer.x - inner.x) * 0.67, y: inner.y + (outer.y - inner.y) * 0.67 }
      };
    }
  }

  return { corners, hCurves, vCurves };
}
